import React, { useState, useEffect, useCallback, useRef } from "react";
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import DatePicker from "../datePicker/datePicker";
import CloseIcon from '@mui/icons-material/Close';
import NoteIcon from '@mui/icons-material/Note';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CategoryIcon from '@mui/icons-material/Category';
import NotesIcon from '@mui/icons-material/Notes';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from "../../api/axiosInstance";
import moment from 'moment';

const ViewEvent = ({ selectedEventId, setFormModal, setSelectedEventId, setSelectedItem, setSelectedTime, fetchData }) => {
    const [event, setEvent] = useState({});
    const [items, setItems] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [descriptions, setDescriptions] = useState([]);
    const [isLoading, setLoading] = useState(true);

    // Ref für das Modal
    const containerRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            try {

                const [eventRes, statusesRes, driversRes, customersRes, itemsRes] = await Promise.all([
                    axiosInstance.get(`events/${selectedEventId}`),
                    axiosInstance.get(`forms/statuses`),
                    axiosInstance.get(`forms/driver`),
                    axiosInstance.get(`forms/customers`),
                    axiosInstance.get(`forms/items`)
                ]);

                setItems(itemsRes.data);
                setStatuses(statusesRes.data);
                setDrivers(driversRes.data);
                setCustomers(customersRes.data);

                setEvent(eventRes.data[0] || {
                    itemId: setSelectedItem ? setSelectedItem.id : null,
                    itemAlias: setSelectedItem ? setSelectedItem.alias + " - " + setSelectedItem.description : null,
                    startDate: setSelectedTime ? setSelectedTime : Date(),
                    endDate: setSelectedTime ? setSelectedTime : Date(),
                    ganztags: 1,
                    description: "",
                    customer: "",
                    driver: setSelectedItem ? setSelectedItem.driver : null,
                    driverAlias: setSelectedItem ? setSelectedItem.driverAlias : null,
                    status: 1,
                    statusName: "Verbucht"
                });

                setLoading(false);
            } catch (err) {
                console.error(err);
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedEventId, setSelectedItem, setSelectedTime]);

    useEffect(() => {
        if (!event.customer) return;

        const fetchDescriptions = async () => {
            try {
                const descriptionsRes = await axiosInstance.get(`forms/descriptions?customer=${event.customer}`);
                setDescriptions(descriptionsRes.data);
            } catch (err) {
                console.error("Fehler beim Abrufen der Beschreibungen:", err);
            }
        };
        fetchDescriptions();
    }, [event.customer]);

    const handleSimpleChange = ({ target }) => {
        const { name, value, type, checked } = target;
        setEvent((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
            ...(name === "startDate" && { endDate: value }),
        }));
    };

    const handleDatePickerChange = (changed) => {
        setEvent(prev => ({ ...prev, ...changed }));
    };

    const handleSelectChange = ({ name, value, label }) => {
        setEvent((prev) => ({
            ...prev,
            [name]: value || "",
            ...(name === "driver" && { driverAlias: label }),
            ...(name === "itemId" && { itemAlias: label }),
            ...(name === "status" && { statusName: label }),
        }));
    };

    const handleAddEvent = async () => {
        try {
            const formattedEvent = {
                ...event,
                startDate: moment(event.startDate).format("yyyy-MM-DD"),
                endDate: moment(event.endDate).format("yyyy-MM-DD"),
                startTime: event.ganztags === 1 ? "00:00:00" : event.startTime,
                endTime: event.ganztags === 1 ? "23:59:00" : event.endTime,
            };

            if (selectedEventId) {
                await axiosInstance.put(`events/${selectedEventId}`, formattedEvent);
            } else {
                await axiosInstance.post(`events`, formattedEvent);
            }
            fetchData()
            setFormModal(false)
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteEvent = async () => {
        try {
            if (selectedEventId) {
                await axiosInstance.delete(`events/${selectedEventId}`);
                setFormModal(false);
                setSelectedEventId(null);
                fetchData()
            }
        } catch (err) {
            console.error("Fehler beim Löschen des Ereignisses:", err);
        }
    };

    const handleDoneEvent = async () => {
        try {
            if (selectedEventId) {
                await axiosInstance.get(`events/done/${selectedEventId}`);
                setEvent(prev => ({ ...prev, done: !prev.done }));
                fetchData()
            }
        } catch (err) {
            console.error("Fehler beim ändern des Ereignisses:", err);
        }
    };

    const handleCloseModal = useCallback(() => {
        setFormModal(false);
        setSelectedEventId(null);
    }, [setFormModal, setSelectedEventId]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleCloseModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleCloseModal]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest(".reactSelect__indicator")) return;
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                handleCloseModal();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleCloseModal]);

    return isLoading ? (<div />) : (
        <div className="modal" ref={containerRef}>
            <div className="topBar">
                <h5>{event.id ? "Ereignis bearbeiten" : "Neues Ereignis hinzufügen"}</h5>
                <button className="closeBtn" onClick={handleCloseModal}>
                    <CloseIcon />
                </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault() }}>
                <div className="group">
                    <BusinessIcon />
                    <Creatable
                        options={customers}
                        type="text"
                        name="customer"
                        className="reactSelect"
                        classNamePrefix="reactSelect"
                        value={event.customer ? { value: event.customer, label: event.customer } : null}
                        onChange={(e) => handleSelectChange({ name: 'customer', value: e?.value })}
                        placeholder="Kunde"
                        isClearable={true}
                        required
                        autoFocus={event.id ? false : true}
                        formatCreateLabel={(inputValue) => `Erstelle "${inputValue}"`}
                    />
                </div>
                <div className="group">
                    <NoteIcon />
                    <Creatable
                        options={descriptions}
                        type="text"
                        name="description"
                        className="reactSelect"
                        classNamePrefix="reactSelect"
                        value={event.description ? { value: event.description, label: event.description } : null}
                        onChange={(e) => handleSelectChange({ name: 'description', value: e?.value })}
                        placeholder="Beschreibung"
                        isClearable={true}
                        formatCreateLabel={(inputValue) => `Erstelle "${inputValue}"`}
                        noOptionsMessage={() => "Keine Ergebnisse"}
                    />
                </div>
                <div className="group">
                    <LocalShippingIcon />
                    <Select
                        options={items}
                        type="text"
                        name="itemId"
                        className="reactSelect"
                        classNamePrefix="reactSelect"
                        value={event.itemId ? { value: event.itemId, label: event.itemAlias } : null}
                        onChange={(e) => handleSelectChange({ name: 'itemId', value: e?.value, label: e?.label })}
                        placeholder="Fahrzeug wählen"
                        isClearable={true}
                        noOptionsMessage={() => "Keine Ergebnisse"}
                    />
                </div>
                <div className="group">
                    <AccessTimeIcon />
                    <DatePicker
                        value={event}
                        onChange={handleDatePickerChange}
                    />
                </div>
                <div className="group">
                    <ManageAccountsIcon />
                    <Select
                        options={drivers}
                        type="text"
                        name="driver"
                        className="reactSelect"
                        classNamePrefix="reactSelect"
                        value={event.driver ? { value: event.driver, label: event.driverAlias } : null}
                        onChange={(e) => handleSelectChange({ name: 'driver', value: e?.value, label: e?.label })}
                        placeholder="Fahrer wählen"
                        isClearable={true}
                        noOptionsMessage={() => "Keine Ergebnisse"}
                    />
                </div>
                <div className="group">
                    <CategoryIcon />
                    <Select
                        options={statuses}
                        type="text"
                        name="status"
                        className="reactSelect"
                        classNamePrefix="reactSelect"
                        value={event.status ? { value: event.status, label: event.statusName } : null}
                        onChange={(e) => handleSelectChange({ name: 'status', value: e?.value, label: e?.label })}
                        placeholder="Status wählen"
                        isSearchable={false}
                    />
                </div>
                <div className="group">
                    <NotesIcon />
                    <textarea name="note" onChange={handleSimpleChange}>{event.note}</textarea>
                </div>
                {
                    event.id &&
                    <div className="group">
                        <PersonAddIcon />
                        <input type="text" value={event.createdByName} disabled />
                    </div>
                }
                <div className="bottom">
                    <div className="left">
                        <button onClick={handleAddEvent}>
                            {event.id ? "Speichern" : "Hinzufügen"}
                        </button>
                        {event.id &&
                            <button className="secondary" onClick={handleDeleteEvent}>Löschen</button>
                        }
                    </div>
                    <div className="right">
                        {event.id &&
                            <button className="secondary" onClick={handleDoneEvent} title={event.done ? "Als ausständig markieren" : "Als erledigt markieren"}>
                                {event.done ?
                                    <CheckCircleIcon /> : <RadioButtonUncheckedIcon />
                                }
                            </button>
                        }
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ViewEvent;